import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  istanbulDayString,
  parseMoneyInput,
  PRODUCT_KINDS,
  returnsEmpty,
  t,
} from '@aquabox/shared';
import {
  useBrands,
  useChannelConfig,
  useChannelMutations,
  useChannels,
  useCreateProductFromMapping,
  useDriverMutations,
  useDryRun,
  useHayatVehicles,
  useManualSession,
  useMappingSuggestion,
  useMe,
  useMappings,
  useProducts,
  useSaveChannelConfig,
  useSaveHayatVehicleMap,
  useScraperMutations,
  useScrapers,
  useTenant,
  useTenantMutations,
  useUnmappedCount,
  useVehicleMutations,
  useVehicles,
} from '../api/queries';
import { AbantEmployeesCard } from './AbantEmployeesCard';
import { Luca } from './Luca';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { Tabs, type TabItem } from '../components/Tabs';
import { useToast } from '../components/Toast';
import type {
  Brand,
  Channel,
  ConnStatus,
  HayatVehiclesResponse,
  HistoryImportResult,
  HistorySkipReason,
  ImportReport,
  LedgerPolicy,
  Product,
  ProductMapping,
  PullSource,
  ScraperStatus,
  Vehicle,
} from '../api/types';

const STATUS_LABEL: Record<ConnStatus, Parameters<typeof t>[0]> = {
  PENDING: 'ch.st.PENDING',
  ACTIVE: 'ch.st.ACTIVE',
  ERROR: 'ch.st.ERROR',
  DISABLED: 'ch.st.DISABLED',
};

const STATUS_COLOR: Record<ConnStatus, string> = {
  PENDING: 'var(--text-secondary)',
  ACTIVE: 'var(--success)',
  ERROR: 'var(--danger)',
  DISABLED: 'var(--warning)',
};

// Manuel oturum rozeti — yalnız `channel.session !== null` olan kanallarda gösterilir
// (bugün tek kaynak GETIR_SU). Renkler ConnStatus'tan AYNI; BOUND=yeşil, EXPIRING=sarı,
// UNBOUND=kırmızı.
const SESSION_STATE_COLOR: Record<'BOUND' | 'EXPIRING' | 'UNBOUND', string> = {
  BOUND: 'var(--success)',
  EXPIRING: 'var(--warning)',
  UNBOUND: 'var(--danger)',
};

const SESSION_STATE_LABEL: Record<'BOUND' | 'EXPIRING' | 'UNBOUND', Parameters<typeof t>[0]> = {
  BOUND: 'ch.manualSession.ui.badge.bound',
  EXPIRING: 'ch.manualSession.ui.badge.expiring',
  UNBOUND: 'ch.manualSession.ui.badge.unbound',
};

type IntegrationsTab = 'baglantilar' | 'urun' | 'arac' | 'gecmis' | 'luca';
const OWNER_ONLY_TABS: readonly IntegrationsTab[] = ['arac', 'gecmis'];

function isIntegrationsTab(value: string | null): value is IntegrationsTab {
  return (
    value === 'baglantilar' ||
    value === 'urun' ||
    value === 'arac' ||
    value === 'gecmis' ||
    value === 'luca'
  );
}

/**
 * Entegrasyonlar (§3.4-3.5).
 *
 * İMZA ANAHTARI BİR KEZ YAZILIR, GERİ OKUNAMAZ: sunucu sırrı hiçbir yanıtta döndürmez, panel
 * yalnız "yazılı mı" bilgisini görür. Sırrı ekranda göstermek, panele erişen herkese o kanaldan
 * sipariş uydurma yetkisi vermek olurdu.
 *
 * Dört sekme (W1): Bağlantılar / Ürün ve fiyat / Araç eşleme / Geçmiş aktarımı. Seçili sekme
 * URL'de (`?tab=`) durur ki başka ekranlardan derin bağlantı çalışsın; geçersiz ya da (OWNER
 * olmayan biri için) yetkisiz değer sessizce Bağlantılar'a düşer. Görünmeyen sekmenin içeriği
 * HİÇ render edilmez — kapalı sekmenin sorguları atılmasın diye.
 */
export function Integrations() {
  const channels = useChannels();
  const unmapped = useUnmappedCount();
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const [connecting, setConnecting] = useState<string | null>(null);
  // `canActivate` sunucuda `INTEGRATION_LIVE`'dan gelir ve tüm kanallarda aynıdır. Liste gelene
  // kadar şerit gösterilmez: açık ortamda bir an görünüp kaybolması yanlış alarm olurdu.
  const coldTest = channels.data !== undefined && !channels.data.some((c) => c.canActivate);
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab: IntegrationsTab =
    requestedTab &&
    isIntegrationsTab(requestedTab) &&
    (!OWNER_ONLY_TABS.includes(requestedTab) || isOwner)
      ? requestedTab
      : 'baglantilar';

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const channelList = channels.data ?? [];
  const unmappedCount = unmapped.data?.count ?? 0;
  const tabItems: TabItem[] = [
    { value: 'baglantilar', label: t('intTabs.connections') },
    { value: 'urun', label: t('intTabs.productPrice'), badge: unmappedCount },
    // Luca: OWNER ve MANAGER okur/eşler; kimlik işlemleri kartın içinde yalnız OWNER'a açık.
    { value: 'luca', label: t('intTabs.luca') },
  ];
  if (isOwner) {
    tabItems.push(
      { value: 'arac', label: t('intTabs.vehicleMapping') },
      { value: 'gecmis', label: t('intTabs.historyImport') },
    );
  }

  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Tabs items={tabItems} value={activeTab} onChange={setTab} />

      {activeTab === 'baglantilar' ? (
        <ConnectionsTab
          channels={channelList}
          coldTest={coldTest}
          connecting={connecting}
          isOwner={isOwner}
          onToggleConnecting={(source) => setConnecting(connecting === source ? null : source)}
        />
      ) : null}
      {activeTab === 'urun' ? (
        <ProductPriceTab channels={channelList} unmapped={unmappedCount} />
      ) : null}
      {/* Uçlar yalnız OWNER'a açık; MANAGER bu sekmeleri hiç görmez (tabItems'ta da yok). */}
      {activeTab === 'arac' && isOwner ? <VehicleMappingTab channels={channelList} /> : null}
      {activeTab === 'gecmis' && isOwner ? <HistoryImportCard /> : null}
      {activeTab === 'luca' ? <Luca /> : null}
    </div>
  );
}

function ConnectionsTab({
  channels,
  coldTest,
  connecting,
  isOwner,
  onToggleConnecting,
}: {
  channels: Channel[];
  coldTest: boolean;
  connecting: string | null;
  isOwner: boolean;
  onToggleConnecting: (source: string) => void;
}) {
  return (
    <>
      {/* Kill switch en üstte: acil durumda aranan yer (CLAUDE.md kural 10). */}
      <ScraperCard />

      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: 720 }}>
        {t('intTabs.connectionsIntro')}
      </p>

      {coldTest ? <ColdTestBanner /> : null}

      <section
        className="kpi-grid"
        style={{ '--kpi-cols': 3, '--kpi-gap': '16px' } as React.CSSProperties}
      >
        {channels.map((c) => (
          <ChannelCard
            key={c.source}
            channel={c}
            open={connecting === c.source}
            isOwner={isOwner}
            onToggle={() => onToggleConnecting(c.source)}
          />
        ))}
      </section>
    </>
  );
}

/** Ürün eşleştirme + Hayat fiyat karşılaştırma — brifing öncesi Hayat kanal kartının içine
 * gömülüydü, bu yüzden bulunmuyordu (kullanıcı şikâyeti). Fiyat paneli yalnız Hayat bağlantısı
 * ACTIVE'ken görünür; değilse tek satır gri açıklama. */
function ProductPriceTab({ channels, unmapped }: { channels: Channel[]; unmapped: number }) {
  const hayat = channels.find((c) => c.source === 'HAYAT_PANEL');
  const priceReady = hayat !== undefined && hayat.status === 'ACTIVE';
  return (
    <>
      <MappingCard unmapped={unmapped} />
      <div
        className="card"
        style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <strong style={{ fontSize: 15 }}>{t('intTabs.priceCompareTitle')}</strong>
        {priceReady && hayat ? (
          <MasterDataImportPanel channel={hayat} />
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            {t('intTabs.priceCompareUnavailable')}
          </p>
        )}
      </div>
    </>
  );
}

function VehicleMappingTab({ channels }: { channels: Channel[] }) {
  return (
    <>
      <HayatVehiclesCard />
      {channels.some((c) => c.source === 'ABANT_PANEL') ? <AbantEmployeesCard /> : null}
    </>
  );
}

const SKIP_LABEL: Record<HistorySkipReason, Parameters<typeof t>[0]> = {
  'no-external-id': 'historyImport.skip.no-external-id',
  'unknown-channel': 'historyImport.skip.unknown-channel',
  'unknown-payment': 'historyImport.skip.unknown-payment',
  'invalid-row': 'historyImport.skip.invalid-row',
  'ingest-failed': 'historyImport.skip.ingest-failed',
};

/**
 * Geçmiş siparişleri aktarma ("Sipariş Sorgulama").
 *
 * İki kaynak desteklenir: HAYAT_PANEL (Telerik tarih, HTML) ve ABANT_PANEL (JSON panel uçtan
 * uç aynı `fetchHistoryJson`). Akış: kaynak → iki tarih → "Önizle" (kuru çalıştırma, hiçbir
 * şey yazmaz) → sayılar → "Aktar" (onay penceresiyle). "Aktar" yalnız GÖRÜNEN önizlemenin
 * aralığı için açılır: tarih ya da kaynak değişirse önce yeniden önizlenir. Kapılar (kill
 * switch, aralık, kilit) sunucuda; buradaki pasiflik yalnız kolaylık.
 */
function HistoryImportCard() {
  const scrapers = useScrapers();
  const { importHistory } = useScraperMutations();
  // İlk yüklemede hangi kaynağın kilidi açıksa onu seç; ikisi de kapalıysa Hayat'la başla
  // (sunucu yine de reddedecek, kilit açıldığında doğru kaynağa geçer).
  const initialSource: PullSource = useMemo(() => {
    const list = scrapers.data ?? [];
    const hayat = list.find((s) => s.source === 'HAYAT_PANEL');
    const abant = list.find((s) => s.source === 'ABANT_PANEL');
    if (hayat?.enabled) return 'HAYAT_PANEL';
    if (abant?.enabled) return 'ABANT_PANEL';
    return 'HAYAT_PANEL';
  }, [scrapers.data]);
  const [source, setSource] = useState<PullSource>(initialSource);
  // Sunucu listesi geç gelirse (yeniden render) seçili kaynağı açık olana çek.
  useEffect(() => {
    const enabledSources = (scrapers.data ?? []).filter((s) => s.enabled).map((s) => s.source);
    if (enabledSources.length === 0) return;
    if (!enabledSources.includes(source)) {
      setSource(enabledSources.includes('HAYAT_PANEL') ? 'HAYAT_PANEL' : 'ABANT_PANEL');
    }
  }, [scrapers.data, source]);
  const enabled =
    (scrapers.data ?? []).find((s: ScraperStatus) => s.source === source)?.enabled ?? false;
  const today = istanbulDayString(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [preview, setPreview] = useState<HistoryImportResult | null>(null);
  const [done, setDone] = useState<HistoryImportResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const run = (dryRun: boolean) => {
    setError(null);
    importHistory.mutate(
      { source, from, to, dryRun },
      {
        onSuccess: (r) => {
          if (dryRun) {
            setPreview(r);
            setDone(null);
          } else {
            setDone(r);
            setPreview(null);
          }
        },
        onError: (err) => setError(err),
      },
    );
  };

  // Kaynak değişti: aynı aralık geçerli görünse de önizleme eski kaynağa ait; sıfırla ki
  // kullanıcı "Aktar"a basıp yanlış kaynağa yanlış aralık göndermesin.
  const onSourceChange = (next: PullSource) => {
    if (next === source) return;
    setSource(next);
    setPreview(null);
    setDone(null);
    setError(null);
  };

  const previewMatches =
    preview !== null && preview.source === source && preview.from === from && preview.to === to;
  const apply = () => {
    if (!preview) return;
    const ok = window.confirm(
      t(`historyImport.${source === 'ABANT_PANEL' ? 'confirmAbant' : 'confirm'}`, {
        from: formatDate(from),
        to: formatDate(to),
        n: preview.created,
      }),
    );
    if (ok) run(false);
  };

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('historyImport.title')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t(`historyImport.${source === 'ABANT_PANEL' ? 'hintAbant' : 'hint'}`)}
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="section-label" style={{ marginBottom: 0 }}>
          {t('historyImport.source')}{' '}
          <select
            value={source}
            onChange={(e) => onSourceChange(e.target.value as PullSource)}
            style={{ height: 40, padding: '0 10px' }}
            data-testid="history-source"
          >
            <option value="HAYAT_PANEL">{t('historyImport.sourceHayat')}</option>
            <option value="ABANT_PANEL">{t('historyImport.sourceAbant')}</option>
          </select>
        </label>
        <label className="section-label" style={{ marginBottom: 0 }}>
          {t('historyImport.from')}{' '}
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            style={{ height: 40, padding: '0 10px' }}
            data-testid="history-from"
          />
        </label>
        <label className="section-label" style={{ marginBottom: 0 }}>
          {t('historyImport.to')}{' '}
          <input
            type="date"
            value={to}
            min={from}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            style={{ height: 40, padding: '0 10px' }}
            data-testid="history-to"
          />
        </label>
        <span style={{ flex: 1 }} />
        <button
          className="btn"
          disabled={importHistory.isPending || !enabled || !from || !to}
          onClick={() => run(true)}
          data-testid="history-preview"
        >
          {t('historyImport.preview')}
        </button>
        <button
          className="btn btn-primary"
          disabled={
            importHistory.isPending || !enabled || !previewMatches || (preview?.created ?? 0) === 0
          }
          onClick={apply}
          data-testid="history-apply"
        >
          {t('historyImport.apply')}
        </button>
      </div>

      {!enabled ? (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
          {t('historyImport.killSwitchOff')}
        </p>
      ) : null}
      {error ? <ErrorLine error={error} /> : null}
      {preview ? <HistoryResult r={preview} /> : null}
      {done ? <HistoryResult r={done} /> : null}
    </div>
  );
}

/** Önizleme ya da aktarım sonucunun sayıları. Kişisel veri yok (sunucu da döndürmez). */
function HistoryResult({ r }: { r: HistoryImportResult }) {
  const rows = r.days.reduce((sum, d) => sum + d.rows, 0);
  const skipped = (Object.entries(r.skipped) as [HistorySkipReason, number][]).filter(
    ([, n]) => n > 0,
  );
  return (
    <div
      data-testid="history-result"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontSize: 12.5,
        color: 'var(--text-secondary)',
        paddingTop: 10,
        borderTop: '1px solid var(--border)',
      }}
    >
      <strong style={{ fontSize: 13 }}>
        {t(r.dryRun ? 'historyImport.previewTitle' : 'historyImport.doneTitle')}
      </strong>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>{t('historyImport.rows', { n: rows })}</span>
        <span style={{ color: 'var(--success)' }}>
          {t(r.dryRun ? 'historyImport.createdDry' : 'historyImport.created', { n: r.created })}
        </span>
        <span>{t('historyImport.existing', { n: r.existing })}</span>
        <span>{t('historyImport.openSkipped', { n: r.openSkipped })}</span>
        {r.unmappedCount > 0 ? (
          <span style={{ color: 'var(--warning)' }}>
            {t('historyImport.unmappedCount', { n: r.unmappedCount })}
          </span>
        ) : null}
      </div>
      {r.unmapped.length > 0 ? (
        <span style={{ color: 'var(--warning)' }}>
          {t('historyImport.unmappedList', { names: r.unmapped.join(', ') })}
        </span>
      ) : null}
      {skipped.map(([reason, n]) => (
        <span key={reason}>
          {t('historyImport.skippedLine', {
            reason: SKIP_LABEL[reason] ? t(SKIP_LABEL[reason]) : reason,
            n,
          })}
        </span>
      ))}
      {r.daysOtherDay.length > 0 ? (
        <span style={{ color: 'var(--warning)' }}>
          {t('historyImport.otherDay', {
            entries: r.daysOtherDay.map((d) => `${formatDate(d.day)}: ${d.otherDay}`).join(', '),
          })}
        </span>
      ) : null}
    </div>
  );
}

/** Tabloda tek satır: Hayat etiketi + gözlemlenen sipariş sayısı + (varsa) bizdeki araç. */
interface HayatVehicleRow {
  label: string;
  orderCount: number;
  vehicleId: string;
}

/**
 * `live` (panelin açılır listesi) ile `observed` (bizim siparişlerde görülen etiketler) ve
 * kayıtlı `map`i TEK satır kümesine birleştirir. Etiket anahtar: aynı etiket ikisinde de
 * geçiyorsa tek satır olur, sipariş sayısı `observed`den gelir (canlı listede sayı yok).
 */
function buildHayatVehicleRows(data: HayatVehiclesResponse | undefined): HayatVehicleRow[] {
  if (!data) return [];
  const byLabel = new Map<string, HayatVehicleRow>();
  for (const o of data.observed ?? []) {
    byLabel.set(o.label, { label: o.label, orderCount: o.orderCount, vehicleId: '' });
  }
  for (const v of data.live ?? []) {
    if (!byLabel.has(v.label))
      byLabel.set(v.label, { label: v.label, orderCount: 0, vehicleId: '' });
  }
  for (const m of data.map ?? []) {
    const existing = byLabel.get(m.hayatLabel);
    if (existing) existing.vehicleId = m.vehicleId;
    else byLabel.set(m.hayatLabel, { label: m.hayatLabel, orderCount: 0, vehicleId: m.vehicleId });
  }
  return [...byLabel.values()].sort(
    (a, b) => b.orderCount - a.orderCount || a.label.localeCompare(b.label, 'tr'),
  );
}

/**
 * Satırları + kullanıcının henüz kaydedilmemiş seçimlerini (`overrides`, etiket → vehicleId)
 * sunucunun beklediği `{ vehicleId: hayatLabel }` gövdesine çevirir. Boş seçim ("—") o satırı
 * gövdeden düşürür — eşlemeyi temizlemek budur.
 */
function computeHayatMapPayload(
  rows: HayatVehicleRow[],
  overrides: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const vehicleId = overrides[row.label] ?? row.vehicleId;
    if (vehicleId) out[vehicleId] = row.label;
  }
  return out;
}

const LIVE_ERROR_LABEL: Record<string, Parameters<typeof t>[0]> = {
  'kill-switch-off': 'hayatVehicles.killSwitchOff',
  'cold-test': 'hayatVehicles.coldTest',
  'already-running': 'hayatVehicles.alreadyRunning',
  'login-failed': 'hayatVehicles.loginFailed',
  'no-session': 'hayatVehicles.noSession',
  'no-consent': 'hayatVehicles.noConsent',
  'fetch-failed': 'hayatVehicles.fetchFailed',
};

function hayatErrorMessage(err: unknown): string {
  const message = (err as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : t('error.internal');
}

const EMPTY_ADD_DRAFT = { plate: '', vehicleLabel: '', driverName: '', phone: '' };

/**
 * "Hayat araçları" — Hayat paneli araç etiketlerini bizdeki araç + şoför kayıtlarına bağlar.
 *
 * Bu eşleme YALNIZ `deriveHayatAction`in yazma akışında okunur (`hayat-reconciler.service.ts`
 * `loadVehicleMap`); burada değiştirmek Hayat'a hiçbir şey GÖNDERMEZ.
 */
export function HayatVehiclesCard() {
  const toast = useToast();
  const source = 'HAYAT_PANEL';
  const data = useHayatVehicles(source);
  const vehiclesQ = useVehicles();
  const saveMap = useSaveHayatVehicleMap(source);
  const { create: createVehicle } = useVehicleMutations();
  const { create: createDriver } = useDriverMutations();

  const rows = useMemo(() => buildHayatVehicleRows(data.data), [data.data]);
  const activeVehicles = (vehiclesQ.data?.items ?? []).filter((v: Vehicle) => v.isActive);

  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const dirty = Object.keys(overrides).length > 0;

  const [addRowLabel, setAddRowLabel] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState(EMPTY_ADD_DRAFT);
  const [addError, setAddError] = useState<{
    step: 'vehicle' | 'driver' | 'map';
    message: string;
  } | null>(null);
  const [addPending, setAddPending] = useState(false);
  // Araç adımı bittikten SONRA şoför ya da eşleme patlarsa "Tekrar dene" bu id'yi kullanır;
  // `createVehicle` İKİNCİ KEZ çağrılmaz (aynı plakayla ikinci araç açılmasın).
  const [createdVehicleId, setCreatedVehicleId] = useState<string | null>(null);

  const effectiveVehicleId = (row: HayatVehicleRow) => overrides[row.label] ?? row.vehicleId;
  const usedElsewhere = (row: HayatVehicleRow, vehicleId: string) =>
    vehicleId !== '' &&
    rows.some((r) => r.label !== row.label && effectiveVehicleId(r) === vehicleId);

  const openAddRow = (row: HayatVehicleRow) => {
    setAddError(null);
    setCreatedVehicleId(null);
    setAddRowLabel(row.label);
    setAddDraft({
      plate: '',
      vehicleLabel: row.label,
      // "ALT BÖLGE" gibi bölge etiketleri kişi adı değildir; şoför adı boş bırakılır.
      driverName: /BÖLGE/i.test(row.label) ? '' : row.label,
      phone: '',
    });
  };

  const saveOverrides = () => {
    setAddError(null);
    saveMap.mutate(computeHayatMapPayload(rows, overrides), {
      onSuccess: () => {
        setOverrides({});
        toast(t('hayatVehicles.saved'));
      },
    });
  };

  const submitAddRow = async (row: HayatVehicleRow) => {
    if (!createdVehicleId && addDraft.plate.trim().length < 5) return;
    setAddPending(true);
    setAddError(null);
    try {
      // Araç adımı DAHA ÖNCE bitmişse (önceki denemede şoför/eşleme patladıysa) burada
      // TEKRAR çağrılmaz — aynı plakayla ikinci araç açılmasın.
      let vehicleId = createdVehicleId;
      if (!vehicleId) {
        try {
          const vehicle = await createVehicle.mutateAsync({
            plate: addDraft.plate.trim(),
            label: addDraft.vehicleLabel.trim() || undefined,
          });
          vehicleId = vehicle.id;
          setCreatedVehicleId(vehicle.id);
        } catch (err) {
          setAddError({ step: 'vehicle', message: hayatErrorMessage(err) });
          return;
        }
      }
      if (addDraft.driverName.trim()) {
        try {
          await createDriver.mutateAsync({
            fullName: addDraft.driverName.trim(),
            phone: addDraft.phone.trim() || undefined,
            vehicleId,
          });
        } catch (err) {
          setAddError({ step: 'driver', message: hayatErrorMessage(err) });
          return;
        }
      }
      try {
        await saveMap.mutateAsync(
          computeHayatMapPayload(rows, { ...overrides, [row.label]: vehicleId }),
        );
      } catch (err) {
        setAddError({ step: 'map', message: hayatErrorMessage(err) });
        return;
      }
      toast(t('hayatVehicles.createAndMapDone'));
      setAddRowLabel(null);
      setCreatedVehicleId(null);
      setOverrides({});
    } finally {
      setAddPending(false);
    }
  };

  const liveErrorLabel = data.data?.liveError ? LIVE_ERROR_LABEL[data.data.liveError] : undefined;

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('hayatVehicles.title')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('hayatVehicles.hint')}
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
        {t('hayatVehicles.autoWriteNote')}
      </p>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
        {t('hayatVehicles.tableIntro')}
      </p>
      {liveErrorLabel ? (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
          {t(liveErrorLabel)}
        </p>
      ) : null}
      {saveMap.isError ? <ErrorLine error={saveMap.error} /> : null}
      {data.isLoading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
          {t('hayatVehicles.unmapped')}
        </p>
      ) : (
        <table
          data-testid="hayat-vehicle-table"
          style={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <thead>
            <tr style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>{t('hayatVehicles.colHayat')}</th>
              <th style={{ padding: '6px 8px' }}>{t('hayatVehicles.colCount')}</th>
              <th style={{ padding: '6px 8px' }}>{t('hayatVehicles.colOurs')}</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const vehicleId = effectiveVehicleId(row);
              return (
                <tr key={row.label} data-testid={`hayat-vehicle-row-${row.label}`}>
                  <td style={{ padding: '6px 8px', fontSize: 13.5 }}>{row.label}</td>
                  <td
                    style={{
                      padding: '6px 8px',
                      fontSize: 13.5,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.orderCount}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <select
                      data-testid={`hayat-vehicle-select-${row.label}`}
                      value={vehicleId}
                      onChange={(e) =>
                        setOverrides((prev) => ({ ...prev, [row.label]: e.target.value }))
                      }
                      style={{ height: 36, minWidth: 180 }}
                    >
                      <option value="">{t('hayatVehicles.placeholder')}</option>
                      {activeVehicles.map((v: Vehicle) => (
                        <option key={v.id} value={v.id} disabled={usedElsewhere(row, v.id)}>
                          {v.label ? `${v.plate} · ${v.label}` : v.plate}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {vehicleId === '' ? (
                      <button
                        className="btn"
                        style={{ height: 32, fontSize: 12.5 }}
                        onClick={() => openAddRow(row)}
                        data-testid={`hayat-vehicle-add-${row.label}`}
                      >
                        {t('hayatVehicles.addRow')}
                      </button>
                    ) : row.vehicleId === vehicleId ? (
                      // Yalnız SUNUCUDAN GELEN (kaydedilmiş) eşleme için rozet — henüz
                      // kaydedilmemiş bir seçim (override) burada YANLIŞLIKLA "eşlendi"
                      // izlenimi vermemeli; "Eşlemeyi kaydet"e basılana kadar bekler.
                      <span
                        data-testid={`hayat-vehicle-mapped-${row.label}`}
                        style={{ color: 'var(--success)', fontWeight: 600, fontSize: 12.5 }}
                      >
                        {t('hayatVehicles.mappedBadge')}
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {addRowLabel !== null ? (
        <div
          className="card"
          style={{
            padding: 14,
            borderColor: 'var(--primary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <strong style={{ fontSize: 13.5 }}>{addRowLabel}</strong>
          <div
            className="form-grid"
            style={
              { '--form-cols': '1fr 1fr 1fr 1fr', '--form-gap': '10px' } as React.CSSProperties
            }
          >
            <input
              value={addDraft.plate}
              onChange={(e) => setAddDraft({ ...addDraft, plate: e.target.value })}
              placeholder={t('hayatVehicles.addPlate')}
              style={{ height: 40 }}
              data-testid="hayat-add-plate"
            />
            <input
              value={addDraft.vehicleLabel}
              onChange={(e) => setAddDraft({ ...addDraft, vehicleLabel: e.target.value })}
              placeholder={t('hayatVehicles.addVehicleLabel')}
              style={{ height: 40 }}
              data-testid="hayat-add-vehicle-label"
            />
            <input
              value={addDraft.driverName}
              onChange={(e) => setAddDraft({ ...addDraft, driverName: e.target.value })}
              placeholder={t('hayatVehicles.addDriverName')}
              style={{ height: 40 }}
              data-testid="hayat-add-driver-name"
            />
            <input
              value={addDraft.phone}
              onChange={(e) => setAddDraft({ ...addDraft, phone: e.target.value })}
              placeholder={t('hayatVehicles.addDriverPhone')}
              style={{ height: 40 }}
              data-testid="hayat-add-phone"
            />
          </div>
          {addError ? (
            <ErrorLine error={{ message: `${addError.step}: ${addError.message}` }} />
          ) : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                setAddRowLabel(null);
                setCreatedVehicleId(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              disabled={(!createdVehicleId && addDraft.plate.trim().length < 5) || addPending}
              onClick={() => {
                const row = rows.find((r) => r.label === addRowLabel);
                if (row) void submitAddRow(row);
              }}
              data-testid="hayat-add-submit"
            >
              {t('hayatVehicles.createAndMap')}
            </button>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
        {!dirty ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('hayatVehicles.noChanges')}
          </span>
        ) : null}
        <button
          className="btn btn-primary"
          disabled={!dirty || saveMap.isPending}
          onClick={saveOverrides}
          data-testid="hayat-save-map"
        >
          {t('hayatVehicles.save')}
        </button>
      </div>
    </div>
  );
}

/** Çekim kaynağı → kill switch bayrağı. Sunucudaki karşılığı `scraper-health.service.ts`. */
const SCRAPER_FLAG: Record<PullSource, string> = {
  HAYAT_PANEL: 'scraperHayat',
  ABANT_PANEL: 'scraperAbant',
  GETIR_SU: 'scraperGetir',
};
const SCRAPER_SOURCES: PullSource[] = ['HAYAT_PANEL', 'ABANT_PANEL', 'GETIR_SU'];

/**
 * Marka paneli veri çekimi — KILL SWITCH (CLAUDE.md kural 10, 11).
 *
 * "Marka durun derse aynı gün duracak anahtar panelde bulunur." Anahtar koda gömülü değil,
 * kiracı bayrağıdır; sunucu her çalıştırmadan önce okur. Kapatmak kod dağıtımı gerektirmez.
 *
 * DURDURMA `PATCH /api/tenant/flags` ile yapılır: bu uç askıda / salt okunur kiracıda da açıktır
 * (kural 11). Çekim ucu (`/toggle`) kısıtlı kiracıda 403 döner; acil durdurma ona bağlanamaz.
 * AÇMA hâlâ `/toggle` üzerinden yapılır.
 *
 * Liste yüklenemezse bayrak durumu Ayarlar'daki kaynaktan (`tenant.flags`) gösterilir ve
 * durdurma aynı uçtan yapılır — acil durumda anahtar sessizce kaybolmaz.
 */
function ScraperCard() {
  const scrapers = useScrapers();
  const tenant = useTenant();
  const { toggle } = useScraperMutations();
  const { updateFlags } = useTenantMutations();
  // Durdurma yıkıcıdır: onaysız çekim kesilmez. Başlatma onaysızdır.
  const [stopping, setStopping] = useState<PullSource | null>(null);
  const sourceName = (source: string) => t(`orderSource.${source}` as Parameters<typeof t>[0]);

  const openStop = (source: PullSource) => {
    // Önceki denemelerin hataları yeni onay penceresine taşınmasın.
    toggle.reset();
    updateFlags.reset();
    setStopping(source);
  };
  const confirmStop = (source: PullSource) => {
    updateFlags.mutate({ [SCRAPER_FLAG[source]]: false }, { onSuccess: () => setStopping(null) });
  };

  const flags = tenant.data?.flags ?? {};
  const fallback = scrapers.isError && tenant.data !== undefined;

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('scraper.title')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('scraper.hint')}
      </p>
      {toggle.isError ? <ErrorLine error={toggle.error} /> : null}
      {scrapers.isLoading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : scrapers.isError ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
            {t('scraper.loadError')}
          </p>
          <div>
            <button className="btn" onClick={() => void scrapers.refetch()}>
              {t('panel.retry')}
            </button>
          </div>
        </div>
      ) : null}
      {fallback ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
          {t('scraper.fallbackHint')}
        </p>
      ) : null}
      {fallback
        ? SCRAPER_SOURCES.map((source) => {
            const on = flags[SCRAPER_FLAG[source]] === true;
            return (
              <div
                key={source}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  paddingTop: 10,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13.5, width: 200 }}>
                  {sourceName(source)}
                </span>
                <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {on ? t('set.on') : t('set.off')}
                </span>
                {on ? (
                  <button
                    className="btn"
                    disabled={updateFlags.isPending}
                    onClick={() => openStop(source)}
                  >
                    {t('scraper.stop')}
                  </button>
                ) : null}
              </div>
            );
          })
        : (scrapers.data ?? []).map((s) => (
            <div
              key={s.source}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                paddingTop: 10,
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13.5, width: 200 }}>
                {sourceName(s.source)}
              </span>
              <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {!s.enabled
                  ? t('scraper.off')
                  : s.offHours
                    ? t('scraper.offHours', { from: s.activeHours?.from ?? '—' })
                    : s.silent
                      ? t('scraper.silent')
                      : t('scraper.running')}
                {s.lastError ? (
                  <div style={{ color: 'var(--danger)' }}>
                    <LastErrorText error={s.lastError} />
                  </div>
                ) : null}
              </div>
              <button
                className={s.enabled ? 'btn' : 'btn btn-primary'}
                disabled={toggle.isPending || updateFlags.isPending}
                onClick={() =>
                  s.enabled
                    ? openStop(s.source)
                    : toggle.mutate({ source: s.source, enabled: true })
                }
              >
                {s.enabled ? t('scraper.stop') : t('scraper.start')}
              </button>
            </div>
          ))}
      {stopping ? (
        <ConfirmDialog
          title={t('scraper.stopTitle', { name: sourceName(stopping) })}
          body={t('scraper.stopBody', { name: sourceName(stopping) })}
          confirmLabel={t('scraper.stop')}
          tone="danger"
          pending={updateFlags.isPending}
          error={
            updateFlags.isError
              ? ((updateFlags.error as { message?: string } | null)?.message ?? t('error.internal'))
              : null
          }
          onConfirm={() => confirmStop(stopping)}
          onCancel={() => setStopping(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Soğuk test şeridi (§0).
 *
 * Kullanıcıya "buton neden kapalı" sorusunun cevabı ekranda durur. Yalnız sunucu
 * `canActivate` false döndüğünde görünür; kapı sunucudadır — bu şerit bilgi, kapı değil.
 */
function ColdTestBanner() {
  return (
    <div
      className="card"
      style={{
        padding: '14px 18px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        borderColor: 'var(--warning)',
        background: 'var(--bg-warning-soft)',
      }}
    >
      <Icon name="alert-triangle" size={18} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <strong style={{ fontSize: 14 }}>{t('ch.coldTestTitle')}</strong>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0 }}>
          {t('ch.coldTestBody')}
        </p>
      </div>
    </div>
  );
}

/**
 * Bağlantı kartı (§14).
 *
 * "Aktifleştir" SUNUCUNUN dediği zaman açılır (`canActivate`), panelin kendi kararıyla değil.
 * Soğuk test fazında kapalı görünür; basılabilseydi bile uç 403 döner.
 */
function ChannelCard({
  channel,
  open,
  isOwner,
  onToggle,
}: {
  channel: Channel;
  open: boolean;
  isOwner: boolean;
  onToggle: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { activate, disable } = useChannelMutations();
  const [consentNote, setConsentNote] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [dryRunConsent, setDryRunConsent] = useState('');
  const [dryRunResult, setDryRunResult] = useState<import('../api/types').DryRunResult | null>(
    null,
  );
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const dryRunTriggerRef = useRef<HTMLButtonElement>(null);
  const dryRunConsentRef = useRef<HTMLInputElement>(null);
  const dryRunRunRef = useRef<HTMLButtonElement>(null);
  const [configText, setConfigText] = useState('');
  const [configJsonError, setConfigJsonError] = useState<string | null>(null);
  const [configDirty, setConfigDirty] = useState(false);
  const ready = channel.hasCredentials && channel.hasConfig;

  const configQuery = useChannelConfig(advancedOpen ? channel.source : undefined);
  const saveConfig = useSaveChannelConfig();
  const dryRun = useDryRun();

  // Config alanı açılınca GET at; kapanınca dirty'yi SIFIRLAMA — toggle kapat/aç sonrası
  // kullanıcının yazdığı metnin korunması gerekir (effect dirty guard bunu yapıyor;
  // burada ek sıfırlama toggle kapama anında kullanıcı değişikliğini silerdi).
  const handleAdvancedToggle = () => {
    setAdvancedOpen((v) => !v);
  };

  // Config query state'ini metin alanına yansıt. Kullanıcı değişiklik yaptıysa dirty
  // korunur ve sunucudan dönen yeni config onun üzerine YAZILMAZ (kaybedilmiş düzenleme
  // olurdu). GET hata verdiğinde metin alanı + kaydet düğmesi pasifleşir, metin
  // boşaltılır; mevcut ErrorLine deseni aşağıda hatayı gösterir.
  useEffect(() => {
    if (!advancedOpen) return;
    if (configQuery.isError) {
      setConfigText('');
      setConfigJsonError(null);
      return;
    }
    if (configDirty) return;
    if (configQuery.data?.config) {
      setConfigText(JSON.stringify(stripManagedKeys(configQuery.data.config), null, 2));
    } else {
      setConfigText('{}');
    }
    setConfigJsonError(null);
  }, [configQuery.dataUpdatedAt, configQuery.isError, advancedOpen]);

  const handleConfigChange = (text: string) => {
    setConfigText(text);
    setConfigDirty(true);
    try {
      JSON.parse(text);
      setConfigJsonError(null);
    } catch (e) {
      setConfigJsonError((e as Error).message);
    }
  };

  const handleSaveConfig = () => {
    if (!configText.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(configText);
    } catch (e) {
      setConfigJsonError((e as Error).message);
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setConfigJsonError(t('chConfig.notObject'));
      return;
    }
    // Araç/şoför/çalışan eşlemeleri başka ekranlardan yönetilir; bu editörden gönderilmez.
    const filtered = stripManagedKeys(parsed as Record<string, unknown>);
    saveConfig.mutate(
      { source: channel.source, config: filtered },
      {
        onSuccess: () => {
          toast(t('chConfig.saved'));
          setConfigDirty(false);
          setConfigJsonError(null);
          // Config sorgusunu tazele — `useSaveChannelConfig` yalnız kanalları invalidate
          // ediyor; editör açıkken kendi kopyasını da güncellemek için burada yeniden çek.
          void qc.invalidateQueries({ queryKey: ['channel-config', channel.source] });
        },
        onError: (err) => {
          setConfigJsonError(
            t('chConfig.saveError', {
              message: (err as { message?: string })?.message ?? t('error.internal'),
            }),
          );
        },
      },
    );
  };

  const canDryRun = channel.hasCredentials && channel.hasConfig;
  const dryRunDisabled = !canDryRun
    ? channel.hasCredentials
      ? t('chDryRun.disabled.noConfig')
      : t('chDryRun.disabled.noCreds')
    : undefined;

  const handleDryRunOpen = () => {
    setDryRunResult(null);
    setDryRunError(null);
    setDryRunConsent('');
    setDryRunOpen(true);
  };

  // Odak yönetimi: açılınca ilk etkileşimli alana, kapanınca tetikleyici düğmeye.
  useEffect(() => {
    if (dryRunOpen) (dryRunConsentRef.current ?? dryRunRunRef.current)?.focus();
  }, [dryRunOpen]);

  const closeDryRun = () => {
    setDryRunOpen(false);
    dryRunTriggerRef.current?.focus();
  };

  const handleDryRunConfirm = () => {
    setDryRunError(null);
    dryRun.mutate(
      {
        source: channel.source,
        ...(dryRunConsent.trim().length >= 10 ? { consentNote: dryRunConsent.trim() } : {}),
      },
      {
        onSuccess: (r) => {
          setDryRunResult(r);
          setDryRunConsent('');
        },
        onError: (err) => {
          setDryRunConsent('');
          const msg = (err as { message?: string })?.message;
          setDryRunError(msg ? t('chDryRun.httpError', { message: msg }) : t('error.internal'));
        },
      },
    );
  };

  const dryRunReady =
    !dryRun.isPending && (!channel.consentAt ? dryRunConsent.trim().length >= 10 : true);

  return (
    <div
      className="card"
      style={{
        padding: 20,
        minHeight: 224,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        borderColor: channel.status === 'ERROR' ? 'var(--danger)' : 'var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 500, fontSize: 12.5 }}>
          {t(`orderSource.${channel.source}` as Parameters<typeof t>[0])}
        </span>
        {/* Manuel oturum rozeti — yalnız `channel.session !== null` olan kanallarda.
            Bugün tek kaynak GETIR_SU; sunucu bu alanı yalnız manual-bearer kanallar için
            doldurur, diğerleri null döner. */}
        {channel.session !== null ? (
          <span
            data-testid={`session-badge-${channel.source}`}
            title={t(SESSION_STATE_LABEL[channel.session.state])}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: 11.5,
              fontWeight: 600,
              color: SESSION_STATE_COLOR[channel.session.state],
              background: 'var(--surface)',
              border: `1px solid ${SESSION_STATE_COLOR[channel.session.state]}`,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: SESSION_STATE_COLOR[channel.session.state],
              }}
            />
            {t(SESSION_STATE_LABEL[channel.session.state])}
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: STATUS_COLOR[channel.status],
          }}
        />
        <span style={{ fontWeight: 600, fontSize: 12.5 }}>{t(STATUS_LABEL[channel.status])}</span>
      </div>

      {channel.session !== null ? (
        <ManualSessionCardBody channel={channel} open={open} onToggle={onToggle} />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 13,
              color: 'var(--text-secondary)',
              flex: 1,
            }}
          >
            <span>{channel.hasCredentials ? t('ch.credsSaved') : t('ch.credsMissing')}</span>
            <span>{channel.hasConfig ? t('ch.configSaved') : t('ch.configMissing')}</span>
            <span>
              {t('ch.lastSuccess')}:{' '}
              {channel.lastSuccessAt ? formatDateTime(channel.lastSuccessAt) : t('ch.never')}
            </span>
            {channel.lastFetchAt ? (
              <span>
                {t('ch.lastFetch')}: {formatDateTime(channel.lastFetchAt)}
              </span>
            ) : null}
          </div>

          {channel.lastError ? (
            <div style={{ fontSize: 12, color: 'var(--danger)' }}>
              <LastErrorText error={channel.lastError} />
              {channel.errorCount > 1 ? ` · ${t('ch.errorCount', { n: channel.errorCount })}` : ''}
            </div>
          ) : null}

          {activate.isError ? <ErrorLine error={activate.error} /> : null}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onToggle}>
              {channel.hasCredentials ? t('common.edit') : t('ch.enterCredentials')}
            </button>
            {channel.status === 'ACTIVE' ? (
              <button
                className="btn"
                disabled={disable.isPending}
                onClick={() => {
                  // Önceki denemenin hatası yeni onay penceresine taşınmasın.
                  disable.reset();
                  setConfirmDisable(true);
                }}
              >
                {t('ch.disable')}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                /* Sunucunun kararı: soğuk test fazında `canActivate` false gelir (§0). */
                disabled={!channel.canActivate || !ready || activate.isPending}
                onClick={() => activate.mutate({ source: channel.source, consentNote })}
              >
                {t('ch.activate')}
              </button>
            )}
          </div>
          {channel.status !== 'ACTIVE' && !channel.canActivate ? (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
              {t('ch.coldTestBlocked')}
            </p>
          ) : null}
          {confirmDisable ? (
            <ConfirmDialog
              title={t('ch.disableTitle', {
                name: t(`orderSource.${channel.source}` as Parameters<typeof t>[0]),
              })}
              body={t('ch.disableBody')}
              confirmLabel={t('ch.disable')}
              tone="danger"
              pending={disable.isPending}
              error={
                disable.isError
                  ? ((disable.error as { message?: string } | null)?.message ?? t('error.internal'))
                  : null
              }
              onConfirm={() =>
                disable.mutate(channel.source, { onSuccess: () => setConfirmDisable(false) })
              }
              onCancel={() => setConfirmDisable(false)}
            />
          ) : null}

          {/* Onay notu yalnız aktifleştirme mümkünken sorulur; kapalıyken boş alan göstermek
              "birazdan açılacak" izlenimi verirdi. */}
          {channel.canActivate && channel.status !== 'ACTIVE' ? (
            <>
              <label
                htmlFor={`ch-consent-${channel.source}`}
                className="section-label"
                style={{ marginBottom: 0 }}
              >
                {t('ch.consentNote')}
              </label>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('ch.consentNoteHint')}
              </p>
              <input
                id={`ch-consent-${channel.source}`}
                value={consentNote}
                onChange={(e) => setConsentNote(e.target.value)}
                style={{ height: 44 }}
              />
            </>
          ) : null}
        </>
      )}

      {/* Parola formu YALNIZ otomatik girişli kanallarda (`session === null` olan kanallar —
          Hayat/Abant) görünür. Manuel bağlantılar kendi formlarını kendi gövdesi içinde açar —
          çünkü bu kaynaklarda `hasCredentials` anlamsız ve sunucu `null` döner; aynı formu
          iki kez koşullamak yerine alana göre böldük. */}
      {open && channel.session === null ? (
        <CredentialsForm channel={channel} onDone={onToggle} />
      ) : null}

      {/* ── Gelişmiş ayarlar — yalnız OWNER ── */}
      {isOwner ? (
        <>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <button
              className="btn"
              style={{ fontSize: 12.5 }}
              onClick={handleAdvancedToggle}
              aria-expanded={advancedOpen}
            >
              {t('chConfig.title')}
              <span aria-hidden="true" style={{ marginLeft: 6 }}>
                {advancedOpen ? '▲' : '▶'}
              </span>
            </button>
          </div>

          {advancedOpen ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                {t('chConfig.warning')}
              </p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                {t('chConfig.editorNote')}
              </p>
              {configQuery.isLoading ? (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {t('common.loading')}
                </span>
              ) : configQuery.isError ? (
                <>
                  <ErrorLine
                    error={
                      configQuery.error ?? {
                        message: t('error.internal'),
                      }
                    }
                  />
                  <textarea
                    id={`ch-config-textarea-${channel.source}`}
                    aria-label={t('chConfig.editorLabel')}
                    value=""
                    readOnly
                    disabled
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      minHeight: 180,
                      padding: 8,
                      resize: 'vertical',
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      opacity: 0.6,
                      cursor: 'not-allowed',
                    }}
                    rows={9}
                  />
                  <button className="btn btn-primary" disabled>
                    {t('chConfig.save')}
                  </button>
                </>
              ) : (
                <>
                  <textarea
                    id={`ch-config-textarea-${channel.source}`}
                    aria-label={t('chConfig.editorLabel')}
                    value={configText}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      minHeight: 180,
                      padding: 8,
                      resize: 'vertical',
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                    }}
                    rows={9}
                  />
                  {configJsonError ? (
                    <span style={{ fontSize: 12, color: 'var(--danger)' }}>
                      {t('chConfig.jsonInvalid', { message: configJsonError })}
                    </span>
                  ) : null}
                  <button
                    className="btn btn-primary"
                    disabled={
                      !configDirty ||
                      configJsonError !== null ||
                      saveConfig.isPending ||
                      !configText.trim()
                    }
                    onClick={handleSaveConfig}
                  >
                    {t('chConfig.save')}
                  </button>
                </>
              )}
            </div>
          ) : null}

          {/* Deneme çekimi */}
          <div>
            <button
              ref={dryRunTriggerRef}
              className="btn"
              style={{ fontSize: 12.5 }}
              disabled={!canDryRun}
              title={dryRunDisabled}
              onClick={handleDryRunOpen}
            >
              {t('chDryRun.button')}
            </button>
          </div>

          {dryRunOpen ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={`ch-dry-run-title-${channel.source}`}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !dryRun.isPending) closeDryRun();
              }}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <strong id={`ch-dry-run-title-${channel.source}`} style={{ fontSize: 14 }}>
                {t('chDryRun.modalTitle')}
              </strong>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                {t('chDryRun.modalBody')}
              </p>

              {!channel.consentAt ? (
                <>
                  <label
                    htmlFor={`ch-dry-run-consent-${channel.source}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    <span className="section-label" style={{ fontSize: 12 }}>
                      {t('chDryRun.consentLabel')}
                    </span>
                    <input
                      ref={dryRunConsentRef}
                      id={`ch-dry-run-consent-${channel.source}`}
                      value={dryRunConsent}
                      onChange={(e) => setDryRunConsent(e.target.value)}
                      placeholder={t('chDryRun.consentHint')}
                      style={{ height: 40 }}
                    />
                  </label>
                  {dryRunConsent.trim().length > 0 && dryRunConsent.trim().length < 10 ? (
                    <span style={{ fontSize: 12, color: 'var(--danger)' }}>
                      {t('chDryRun.consentTooShort')}
                    </span>
                  ) : null}
                </>
              ) : null}

              {dryRunResult ? (
                dryRunResult.ok ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    <span style={{ color: 'var(--success)' }}>
                      {t('chDryRun.ok', { n: dryRunResult.itemCount })}
                    </span>
                    <span>
                      {t('chDryRun.created', { c: dryRunResult.wouldCreate })} ·{' '}
                      {t('chDryRun.updated', { u: dryRunResult.wouldUpdate })} ·{' '}
                      {t('chDryRun.skipped', { s: dryRunResult.wouldSkip })}
                    </span>
                    {dryRunResult.unmappedProducts.length > 0 ? (
                      <div>
                        <strong style={{ fontSize: 12 }}>{t('chDryRun.unmappedProducts')}:</strong>
                        <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                          {dryRunResult.unmappedProducts.map((p) => (
                            <li key={p}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {dryRunResult.unknownChannels.length > 0 ? (
                      <div>
                        <strong style={{ fontSize: 12 }}>{t('chDryRun.unknownChannels')}:</strong>
                        <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                          {dryRunResult.unknownChannels.map((c) => (
                            <li key={c}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {dryRunResult.emptyFields.length > 0 ? (
                      <div>
                        <strong style={{ fontSize: 12 }}>{t('chDryRun.emptyFields')}:</strong>
                        <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                          {dryRunResult.emptyFields.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--danger)' }}>
                    {t('chDryRun.failed', { error: dryRunResult.error ?? '' })}
                  </span>
                )
              ) : dryRunError ? (
                <ErrorLine error={{ message: dryRunError }} />
              ) : null}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={closeDryRun}>
                  {t('common.cancel')}
                </button>
                <button
                  ref={dryRunRunRef}
                  className="btn btn-primary"
                  disabled={!dryRunReady}
                  onClick={handleDryRunConfirm}
                >
                  {dryRun.isPending ? t('chDryRun.running') : t('chDryRun.run')}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Hayat panelinden ürün/fiyat listesi çekme + bizim ürünlerle karşılaştırma paneli.
 *
 * AKIŞ (round 2 — kapsam daraltıldı):
 *   1. Kullanıcı "Kuru çalıştır" der → sunucu fiyatları karşılaştırır, sonucu raporlar.
 *   2. Rapor görünür: değişecek fiyatlar tablosu, eşleşmeyenler, belirsiz eşleşmeler.
 *   3. Kullanıcı "Fiyatları güncelle" der → onay penceresi sonrası gerçek yazım tetiklenir.
 *
 * NEDEN İKİ AŞAMA: hayat paneli ~10 ürün; bir kerede yazmak "yanlış ürüne yazıldı"
 * senaryolarında geri alma derdi olurdu. Kuru çalıştırma sonucu görünmeden "Fiyatları
 * güncelle" kapalı kalır.
 *
 * YENİ ÜRÜN AÇILMAZ, yeni marka AÇILMAZ — eşleşmeyen satırlar `unmatched` listesinde
 * kalır; patron Ürünler ekranından kendisi açar.
 */
function MasterDataImportPanel({ channel }: { channel: Channel }) {
  const toast = useToast();
  const { importMasterData } = useScraperMutations();
  const [report, setReport] = useState<ImportReport | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const trigger = (dryRun: boolean, create?: string[]): void => {
    setLastError(null);
    importMasterData.mutate(
      { source: channel.source, dryRun, create },
      {
        onSuccess: (r) => {
          setReport(r);
          if (create && create.length > 0 && r.created.length > 0) {
            toast(t('import.createdFromUnmatched', { n: r.created.length }));
          }
        },
        onError: (err) => {
          // `onError` round 3'te eklendi — sunucu mesajı kırmızı görünür. Mutation'ın
          // reddedildiği kırmızı hata gösterilir; `lastError` toast yerine kart altında
          // kalıcı durur, böylece "karşılaştırmayı göremedim" şikâyeti oluşmaz.
          const message = (err as { message?: string } | null)?.message ?? t('error.internal');
          setLastError(message);
        },
      },
    );
  };

  const apply = (): void => {
    if (window.confirm(t('import.applyConfirm'))) {
      trigger(false);
    }
  };

  /**
   * Eşleşmeyen satırlardan seçilenleri ürün olarak açar, ardından karşılaştırmayı YENİDEN
   * koşar — yeni açılan ürünler bir sonraki turda artık `unmatched` değil `priceChanges`
   * listesinde görünür ve gerçek fiyatları rapora düşer. Bu döngü patron için
   * "aç → karşılaştır" tek tık yerine "aç + aynı ekranda yeni sonuç" verir.
   */
  const createAll = (): void => {
    if (!report || report.unmatched.length === 0) return;
    trigger(
      false,
      report.unmatched.map((u) => u.name),
    );
  };

  const showApplied = report && !report.dryRun && report.ok;

  return (
    <div
      style={{
        marginTop: 4,
        paddingTop: 10,
        borderTop: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <span className="section-label">{t('import.compareTitle')}</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn" disabled={importMasterData.isPending} onClick={() => trigger(true)}>
          {t('import.dryRun')}
        </button>
        {/* Uygula düğmesi yalnız kuru çalıştırma sonucu görünür, henüz uygulanmamışsa
            ve en az bir değişiklik varsa. */}
        {report?.dryRun && report.ok && !showApplied && report.priceChanges.length > 0 ? (
          <button className="btn btn-primary" disabled={importMasterData.isPending} onClick={apply}>
            {t('import.apply')}
          </button>
        ) : null}
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('import.dryRunHelp')}</span>

      {report ? (
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            background: 'var(--surface)',
            padding: 10,
            borderRadius: 6,
          }}
        >
          <div>
            <strong>{t('import.compareTitle')}</strong>
            {' · '}
            {t('import.summary', {
              read: report.read,
              unchanged: report.unchanged,
              priceChanges: report.priceChanges.length,
              unmatched: report.unmatched.length,
            })}
          </div>

          {report.priceChanges.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <em>{t('import.priceChangesHeading')}:</em>
              <table
                style={{
                  marginTop: 4,
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th>{t('import.colProduct')}</th>
                    <th>{t('import.colCurrent')}</th>
                    <th>{t('import.colHayat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.priceChanges.map((p) => (
                    <tr key={p.productId}>
                      <td>{p.name}</td>
                      <td>{formatMoney(p.currentPrice)}</td>
                      <td>{formatMoney(p.hayatPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {report.unmatched.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <em>
                {t('import.unmatchedHeading')} ({report.unmatched.length})
              </em>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {report.unmatched.map((u, i) => (
                  <li
                    key={i}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
                  >
                    <span style={{ flex: 1 }}>
                      {u.name} · {formatMoney(u.hayatPrice)}
                    </span>
                    <button
                      type="button"
                      className="btn"
                      style={{ height: 28, padding: '0 10px', fontSize: 12 }}
                      disabled={importMasterData.isPending}
                      onClick={() => trigger(false, [u.name])}
                      data-testid={`import-add-${i}`}
                    >
                      {t('import.unmatchedAddOne')}
                    </button>
                  </li>
                ))}
              </ul>
              {/* Round 3: tüm eşleşmeyenleri TEK TUR'da ürün olarak ekle. dryRun=true iken
                  uygulanmaz (sunucu create'i yalnız dryRun=false ile işler). */}
              {!report.dryRun ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: 6, height: 32, fontSize: 12.5 }}
                  disabled={importMasterData.isPending}
                  onClick={createAll}
                  data-testid="import-add-all"
                >
                  {t('import.unmatchedAddAll')}
                </button>
              ) : null}
            </div>
          ) : null}

          {report.skippedUnknownKind && report.skippedUnknownKind.length > 0 ? (
            <div style={{ marginTop: 6, color: 'var(--warning)' }}>
              <em>{t('import.unknownKindSkipped', { name: '' })}</em>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {report.skippedUnknownKind.map((s, i) => (
                  <li key={i}>
                    {s.name} · {formatMoney(s.hayatPrice)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {report.failed.length > 0 ? (
            <div style={{ marginTop: 6, color: 'var(--danger)' }}>
              <em>{t('import.failedHeading', { count: report.failed.length })}</em>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {report.failed.map((f) => (
                  <li key={f.productId ?? f.name}>
                    {f.productId ? (
                      <>
                        {f.name} · {t(`import.failReason.${f.reason}`)}
                      </>
                    ) : (
                      t('import.createFailed', {
                        name: f.name,
                        reason: t(`import.failReason.${f.reason}`),
                      })
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {report.ambiguous.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <em>{t('import.ambiguousHeading')}</em>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {report.ambiguous.map((a, i) =>
                  a.kind === 'multiple-products' ? (
                    <li key={i}>
                      {a.name} · {formatMoney(a.hayatPrice)} ·{' '}
                      {t('import.candidates', { count: a.candidateProductIds.length })}
                    </li>
                  ) : (
                    <li key={i}>
                      {a.name} ·{' '}
                      {t('import.multipleRows', {
                        rows: a.rows
                          .map((r) => `${r.name} (${formatMoney(r.hayatPrice)})`)
                          .join(', '),
                      })}
                    </li>
                  ),
                )}
              </ul>
            </div>
          ) : null}

          {report.skipped.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <em>{t('import.skippedHeading', { count: report.skipped.length })}</em>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {report.skipped.map((s) => (
                  <li key={s.productId}>
                    {s.name} · {t(`import.skipReason.${s.reason}`)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {showApplied ? (
            <div style={{ marginTop: 8, color: 'var(--success)' }}>
              ✓ {t('import.appliedNotice')}
            </div>
          ) : null}
        </div>
      ) : null}

      {lastError ? (
        <p
          data-testid="import-error"
          style={{
            fontSize: 13,
            color: 'var(--danger)',
            background: 'var(--surface)',
            padding: 10,
            borderRadius: 6,
            margin: 0,
          }}
        >
          <Icon name="alert-circle" size={14} /> {lastError}
        </p>
      ) : null}
      {importMasterData.isError && !lastError ? <ErrorLine error={importMasterData.error} /> : null}
    </div>
  );
}

/**
 * Giriş bilgisi formu.
 *
 * Parola YALNIZ YAZILIR: sunucu şifreli saklar ve hiçbir yanıtta döndürmez, bu yüzden alan
 * mevcut değerle DOLDURULMAZ — doldurulsaydı panele erişen herkes bayi parolasını okurdu.
 */
function CredentialsForm({ channel, onDone }: { channel: Channel; onDone: () => void }) {
  const { saveConnection } = useChannelMutations();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
      <span className="section-label">{t('ch.username')}</span>
      <input
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoComplete="off"
        style={{ height: 44 }}
      />
      <span className="section-label">{t('ch.password')}</span>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        style={{ height: 44 }}
      />
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {t('ch.credsWriteOnly')}
      </p>
      {saveConnection.isError ? <ErrorLine error={saveConnection.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={username.trim() === '' || password.trim() === '' || saveConnection.isPending}
          onClick={() =>
            saveConnection.mutate(
              {
                source: channel.source,
                credentials: { username: username.trim(), password },
              },
              {
                onSuccess: () => {
                  // Parolayı bellekte tutma: form kapanırken alanlar temizlenir.
                  setPassword('');
                  onDone();
                },
              },
            )
          }
        >
          {t('ch.saveCredentials')}
        </button>
      </div>
    </div>
  );
}

/**
 * Manuel oturum kartının gövdesi — yalnız GETIR_SU gibi `channel.session !== null` olan
 * kanallarda kullanılır. Üç parça:
 *   1) Oturum özet satırı (UNBOUND'a kısa uyarı; BOUND/EXPIRING'a bitiş zamanı).
 *   2) Aksiyon düğmeleri: "Bağla" / "Düzenle" ve BOUND-EXPIRING'de "Oturumu kopar".
 *   3) `open` true ise form (yardım metni + jeton + onay notu + bağla/vazgeç).
 *
 * "Oturumu kopar" app'ın diyalog desenini kullanır (role="dialog", odak yönetimi, Escape);
 * tarayıcının `window.confirm`'ına DÜŞMEZ — sır silen geri alınamaz işlem onaysız geçmez.
 */
function ManualSessionCardBody({
  channel,
  open,
  onToggle,
}: {
  channel: Channel;
  open: boolean;
  onToggle: () => void;
}) {
  const { unbind } = useManualSession();
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const disconnectTriggerRef = useRef<HTMLButtonElement>(null);
  const disconnectCancelRef = useRef<HTMLButtonElement>(null);
  const disconnectConfirmRef = useRef<HTMLButtonElement>(null);

  // Diyalog açılınca "Vazgeç"e odakla; kapanınca tetikleyici düğmeye dön. Escape kapatır.
  useEffect(() => {
    if (disconnectOpen) disconnectCancelRef.current?.focus();
  }, [disconnectOpen]);
  useEffect(() => {
    if (!disconnectOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDisconnectOpen(false);
        disconnectTriggerRef.current?.focus();
      } else if (e.key === 'Tab') {
        // Tab/Shift+Tab odak halkası: diyalog içinde iki düğme arasında döner.
        const cancelBtn = disconnectCancelRef.current;
        const confirmBtn = disconnectConfirmRef.current;
        if (!cancelBtn || !confirmBtn) return;
        e.preventDefault();
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === cancelBtn) confirmBtn.focus();
          else cancelBtn.focus();
        } else {
          if (active === confirmBtn) cancelBtn.focus();
          else confirmBtn.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [disconnectOpen]);

  const closeDisconnect = () => {
    setDisconnectOpen(false);
    disconnectTriggerRef.current?.focus();
  };

  const handleDisconnectConfirm = () => {
    setDisconnectOpen(false);
    unbind.mutate(
      { source: channel.source },
      {
        onSuccess: () => {
          // UNBOUND'a düşünce `disconnectTriggerRef` düğmesi DOM'dan kalkar;
          // o durumda odak "Oturumu bağla" düğmesine geçsin (her zaman DOM'da).
          // invalidate → refetch → render henüz tamamlanmamış olabilir; bir sonraki
          // frame'i bekleyip DOM güncellendikten sonra odakla.
          requestAnimationFrame(() => {
            const trigger = disconnectTriggerRef.current;
            if (trigger && document.contains(trigger)) {
              trigger.focus();
            } else {
              const openBtn = document.querySelector<HTMLButtonElement>(
                `[data-testid="session-open-${channel.source}"]`,
              );
              openBtn?.focus();
            }
          });
        },
      },
    );
  };

  const session = channel.session;
  const boundOrExpiring = session?.state === 'BOUND' || session?.state === 'EXPIRING';
  const expiresLine = session?.expiresAt
    ? session.state === 'EXPIRING'
      ? t('ch.manualSession.ui.expiresAtSoon', { time: formatDateTime(session.expiresAt) })
      : t('ch.manualSession.ui.expiresAt', { time: formatDateTime(session.expiresAt) })
    : null;

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 13,
          color: 'var(--text-secondary)',
          flex: 1,
        }}
      >
        {session === null ? null : session.state === 'UNBOUND' ? (
          <span style={{ color: 'var(--danger)' }}>{t('ch.manualSession.ui.badge.unbound')}</span>
        ) : expiresLine ? (
          <span data-testid={`session-expires-${channel.source}`}>{expiresLine}</span>
        ) : null}
        {channel.lastError ? (
          <div style={{ fontSize: 12, color: 'var(--danger)' }}>
            <LastErrorText error={channel.lastError} />
            {channel.errorCount > 1 ? ` · ${t('ch.errorCount', { n: channel.errorCount })}` : ''}
          </div>
        ) : null}
        <span>
          {t('ch.lastSuccess')}:{' '}
          {channel.lastSuccessAt ? formatDateTime(channel.lastSuccessAt) : t('ch.never')}
        </span>
      </div>

      {unbind.isError ? <ErrorLine error={unbind.error} /> : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={onToggle}
          data-testid={`session-open-${channel.source}`}
        >
          {boundOrExpiring ? t('common.edit') : t('ch.manualSession.ui.title')}
        </button>
        {boundOrExpiring ? (
          <button
            ref={disconnectTriggerRef}
            className="btn"
            onClick={() => setDisconnectOpen(true)}
            data-testid={`session-disconnect-${channel.source}`}
            disabled={unbind.isPending}
          >
            {t('ch.manualSession.ui.disconnect')}
          </button>
        ) : null}
      </div>

      {open ? (
        <ManualSessionForm channel={channel} onCancel={onToggle} onConnected={onToggle} />
      ) : null}

      {disconnectOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`session-disconnect-title-${channel.source}`}
          data-testid={`session-disconnect-dialog-${channel.source}`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              closeDisconnect();
            }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(16,30,56,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={(e) => {
            // Dış tıklama ile kapatma — sır silen işlem, bilinçsiz tıklayla tetiklenmesin.
            if (e.target === e.currentTarget) closeDisconnect();
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: 'var(--r-menu)',
              padding: 20,
              width: 'min(440px, 92vw)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <strong id={`session-disconnect-title-${channel.source}`} style={{ fontSize: 15 }}>
              {t('ch.manualSession.ui.disconnectTitle')}
            </strong>
            <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              {t('ch.manualSession.ui.disconnectBody')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                ref={disconnectCancelRef}
                className="btn"
                onClick={closeDisconnect}
                data-testid={`session-disconnect-cancel-${channel.source}`}
              >
                {t('common.cancel')}
              </button>
              <button
                ref={disconnectConfirmRef}
                className="btn btn-primary"
                onClick={handleDisconnectConfirm}
                disabled={unbind.isPending}
                data-testid={`session-disconnect-confirm-${channel.source}`}
              >
                {t('ch.manualSession.ui.disconnect')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * Manuel oturum bağlama formu. Tek jeton alanı + (ilk bağlamada) onay notu.
 *
 * GÜVENLİK: jeton state'te, cache'te, logda, toast'ta KALMAZ. `onChange`'de durmaz
 * (aksiyon için lazım); her başarı/hatada alan sıfırlanır ve form kapanır. Hata
 * metni `ErrorLine` ile sunucunun kendi Türkçe yanıtından gelir — jeton parçası taşımaz
 * (sunucu tarafı `manual-session.ts` de jetonu yankılamaz).
 */
function ManualSessionForm({
  channel,
  onCancel,
  onConnected,
}: {
  channel: Channel;
  onCancel: () => void;
  onConnected: () => void;
}) {
  const { bind } = useManualSession();
  const [token, setToken] = useState('');
  const [consentNote, setConsentNote] = useState('');
  const [bindError, setBindError] = useState<string | null>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const tokenFieldId = `session-token-${channel.source}`;
  const consentFieldId = `session-consent-${channel.source}`;
  const consentWarnId = `session-consent-warn-${channel.source}`;

  useEffect(() => {
    tokenRef.current?.focus();
  }, []);

  const needsConsent = channel.consentAt === null;
  const consentTrimmed = consentNote.trim();
  const consentTooShort = needsConsent && consentTrimmed.length < 10;
  // Boşken göstermek kullanıcıyı yorar; 1..9 arasında uyarı çıkar. 10+ olunca uyarı kalkar.
  const consentRequiredVisible =
    needsConsent && consentTrimmed.length >= 1 && consentTrimmed.length < 10;
  // Yalnız "Bearer" (ya da boş) gönderilecek bir jeton değildir.
  const tokenEmpty = token.trim().replace(/^Bearer\s*$/i, '') === '';
  const canSubmit = !tokenEmpty && !consentTooShort && !bind.isPending;

  const reset = () => {
    setToken('');
    setConsentNote('');
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Yapıştırılan metin "Bearer xyz" önekiyle gelirse soy; trim + boşsa PUT atma.
    const submittedToken = token.trim().replace(/^Bearer\s+/i, '');
    if (submittedToken === '' || /^Bearer\s*$/i.test(submittedToken)) return;
    const submittedConsent = consentNote.trim();
    // Jetonu GÖNDERİM ANINDA bellekten/domdan sil — submit'ten sonra "submittedToken"
    // artık yerel değişken; yeniden render'da `token` zaten boş (state sıfırlandı).
    const sentSnapshot = submittedToken;
    reset();
    setBindError(null);
    bind.mutate(
      {
        source: channel.source,
        token: sentSnapshot,
        ...(needsConsent && submittedConsent.length >= 10 ? { consentNote: submittedConsent } : {}),
      },
      {
        onSuccess: () => onConnected(),
        onError: (err) => {
          // Hata olsa bile jetonu formdan ve state'ten sil — bağlantı denemesi bitti,
          // aynı jetonu tutmak sır yüzeyi olur. Form açık kaldığı için mutation
          // gc'lenmez; `bind.reset()` `variables.token`'ı cache'ten düşürür, hata
          // metni yerel state'te kalır.
          reset();
          setBindError((err as { message?: string } | null)?.message ?? '');
          bind.reset();
        },
      },
    );
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}
      data-testid={`session-form-${channel.source}`}
    >
      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)', margin: 0 }}>
        {t('ch.manualSession.ui.intro')}
      </p>

      <details style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        <summary style={{ cursor: 'pointer' }}>{t('ch.manualSession.ui.helpTitle')}</summary>
        <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-line', lineHeight: 1.55 }}>
          {t('ch.manualSession.ui.helpBody')}
        </p>
      </details>

      <label htmlFor={tokenFieldId} className="section-label" style={{ marginBottom: 0 }}>
        {t('ch.manualSession.ui.tokenLabel')}
      </label>
      <input
        ref={tokenRef}
        id={tokenFieldId}
        type="password"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder={t('ch.manualSession.ui.tokenPlaceholder')}
        autoComplete="off"
        spellCheck={false}
        data-testid={tokenFieldId}
        style={{ height: 44 }}
      />

      {needsConsent ? (
        <>
          <label htmlFor={consentFieldId} className="section-label" style={{ marginBottom: 0 }}>
            {t('ch.manualSession.ui.consentLabel')}
          </label>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
            {t('ch.manualSession.ui.consentHint')}
          </p>
          <input
            id={consentFieldId}
            value={consentNote}
            onChange={(e) => setConsentNote(e.target.value)}
            aria-describedby={consentRequiredVisible ? consentWarnId : undefined}
            data-testid={consentFieldId}
            style={{ height: 44 }}
          />
          {consentRequiredVisible ? (
            <span
              id={consentWarnId}
              data-testid={`session-consent-required-${channel.source}`}
              style={{ fontSize: 12, color: 'var(--danger)' }}
            >
              {t('ch.manualSession.ui.consentRequired')}
            </span>
          ) : null}
        </>
      ) : null}

      {bindError !== null ? <ErrorLine error={{ message: bindError }} /> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn"
          onClick={() => {
            reset();
            onCancel();
          }}
        >
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid={`session-connect-${channel.source}`}
        >
          {bind.isPending ? t('ch.manualSession.ui.connecting') : t('ch.manualSession.ui.connect')}
        </button>
      </div>
    </div>
  );
}

/** Ürün eşleştirme (§3.4 B). Eşleşmeyen satır siparişi düşürmez, amber ile görünür. */
function MappingCard({ unmapped }: { unmapped: number }) {
  const mappings = useMappings();
  const products = useProducts();
  const brands = useBrands();
  const { mapProduct } = useChannelMutations();
  const createProduct = useCreateProductFromMapping();
  const toast = useToast();
  const rows = mappings.data ?? [];
  // TÜR FİLTRESİ YOK: sunucu her ürün türüne eşlemeyi kabul eder (`channels.service.ts#mapProduct`).
  // 19L sabiti 15L cam etiketini yalnız bir 19L ürüne bağlatıyordu — 15L satışı 19L defterine
  // düşerdi (ADR 2026-09-20). Yanlış türe eşlemeyi seçenek etiketindeki tür adı önler.
  const allProducts = products.data?.items ?? [];
  const brandList = brands.data ?? [];

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>{t('ch.mappingTitle')}</strong>
        {unmapped > 0 ? (
          <span className="badge-amber">{t('ch.unmapped', { n: unmapped })}</span>
        ) : null}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('ch.mappingHint')}
      </p>
      {mapProduct.isError ? <ErrorLine error={mapProduct.error} /> : null}
      {createProduct.isError ? <ErrorLine error={createProduct.error} /> : null}
      {rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('ch.noMappings')}</p>
      ) : (
        rows.map((row) => (
          <MappingRow
            key={row.id}
            row={row}
            products={allProducts}
            brands={brandList}
            pending={mapProduct.isPending}
            onPick={(productId) => mapProduct.mutate({ id: row.id, productId })}
            onPickPolicy={(ledgerPolicy) =>
              mapProduct.mutate(
                { id: row.id, productId: row.productId, ledgerPolicy },
                { onSuccess: () => toast(t('extLedgerUi.policySaved')) },
              )
            }
            onCreated={() => createProduct.reset()}
          />
        ))
      )}
    </div>
  );
}

/**
 * Eşleştirme satırı. Kaynak markası (HAYAT_PANEL → "H", ABANT_PANEL → "A") ilk açılışta
 * ürün listesini o markanın ürünlerine süzer; kullanıcı "Tüm ürünleri göster" anahtarını
 * açarsa tüm damacanalar listelenir. Markasız ürünler HER ZAMAN görünür — yanlış
 * eşleştirme kaynağın markasına bağlı olmamalı.
 *
 * Eşleşen marka YOKSA (yeni marka kodu eklenmedi) liste yine tüm ürünleri gösterir;
 * kullanıcı boş ekranla karşılaşıp "seçenek yok" diye düşünmesin.
 *
 * Round 3: eşleşmiş satırda "Değiştir" düğmesi (açılır listeye dönmek için).
 * Eşleşmemiş satırda "Yeni ürün olarak ekle" düğmesi (inline form, öneri ucundan dolar).
 */
function MappingRow({
  row,
  products,
  brands,
  pending,
  onPick,
  onPickPolicy,
  onCreated,
}: {
  row: ProductMapping;
  products: Product[];
  brands: Brand[];
  pending: boolean;
  onPick: (productId: string | null) => void;
  onPickPolicy: (policy: LedgerPolicy | null) => void;
  onCreated: () => void;
}) {
  const expectedCode = sourceBrandCode(row.source);
  const defaultBrand = expectedCode
    ? brands.find((b) => b.isActive && b.code === expectedCode)
    : undefined;
  const [showAllLocal, setShowAllLocal] = useState(false);
  const [editing, setEditing] = useState(!row.product); // eşleşmemişse doğrudan düzenleme modunda
  const [adding, setAdding] = useState(false);
  const grouped = useMemo(() => groupByBrand(products, brands), [products, brands]);

  // Round 3: eşleşen markada AKTİF ürün yoksa otomatik tüm aktif ürünler listelenir;
  // kullanıcı boş ekranla karşılaşmasın. "Tüm ürünleri göster" anahtarı her zaman açılabilir.
  const brandHasProducts = Boolean(defaultBrand && (grouped.get(defaultBrand.id) ?? []).length > 0);

  // Varsayılan: eşleşen markanın ürünleri + (markasız varsa) + anahtar açılırsa diğer markalar.
  const options = useMemo(() => {
    if (showAllLocal || !defaultBrand || !brandHasProducts) {
      return grouped;
    }
    const filtered = new Map<string, Product[]>();
    filtered.set(defaultBrand.id, grouped.get(defaultBrand.id) ?? []);
    const unbranded = grouped.get('__unbranded__');
    if (unbranded) filtered.set('__unbranded__', unbranded);
    return filtered;
  }, [grouped, defaultBrand, showAllLocal, brandHasProducts]);

  const [picked, setPicked] = useState<string>('');

  if (adding) {
    return (
      <NewProductForm
        row={row}
        defaultBrand={defaultBrand}
        brandHasProducts={brandHasProducts}
        onCancel={() => {
          setAdding(false);
          onCreated();
        }}
      />
    );
  }

  return (
    <div
      className="rowgrid-stack"
      data-testid={`mapping-row-${row.source}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr 36px 320px',
        alignItems: 'center',
        gap: 12,
        minHeight: 58,
        padding: '0 10px',
        borderLeft: row.productId ? '3px solid transparent' : '3px solid var(--warning)',
        background: row.productId ? undefined : 'var(--bg-warning-soft)',
        borderRadius: 'var(--r-row)',
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 500 }}>
        {t(`orderSource.${row.source}` as Parameters<typeof t>[0])}
      </span>
      <span style={{ fontWeight: 500, fontSize: 14.5 }}>“{row.externalName}”</span>
      <Icon name="link" size={16} />
      {row.product && !editing ? (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{row.product.name}</span>
          <button
            type="button"
            className="btn"
            data-testid={`mapping-change-${row.source}`}
            onClick={() => setEditing(true)}
          >
            {t('catalog.changeMapping')}
          </button>
        </span>
      ) : (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            data-testid={`mapping-select-${row.source}`}
            value={picked}
            disabled={pending}
            onChange={(e) => setPicked(e.target.value)}
            style={{ height: 40, flex: '1 1 200px', minWidth: 200 }}
          >
            <option value="">—</option>
            {[...options.entries()].map(([brandKey, items]) => {
              if (items.length === 0) return null;
              const label =
                brandKey === '__unbranded__'
                  ? t('brand.unbrandedBadge')
                  : t('brand.badgeTitle', {
                      code: brands.find((b) => b.id === brandKey)?.code ?? '',
                      name: brands.find((b) => b.id === brandKey)?.name ?? '',
                    });
              return (
                <optgroup key={brandKey} label={label}>
                  {items.map((p) => (
                    <option key={p.id} value={p.id}>
                      {t('prod.nameWithKind', { name: p.name, kind: t(`productKind.${p.kind}`) })}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          <button
            className="btn btn-primary"
            disabled={pending || picked === ''}
            onClick={() => {
              onPick(picked);
              setEditing(false);
            }}
            data-testid={`mapping-save-${row.source}`}
          >
            {t('common.save')}
          </button>
          {expectedCode ? (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12.5,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                data-testid={`mapping-toggle-all-${row.source}`}
                checked={showAllLocal}
                onChange={(e) => setShowAllLocal(e.target.checked)}
              />
              {t('ch.showAllProducts')}
            </label>
          ) : null}
        </span>
      )}
      {!row.product ? (
        <div style={{ gridColumn: '1 / -1', marginTop: 6, fontSize: 12.5 }}>
          <button
            type="button"
            className="btn"
            data-testid={`mapping-add-${row.source}`}
            onClick={() => setAdding(true)}
          >
            {t('catalog.addAsProduct')}
          </button>
          {!brandHasProducts && expectedCode ? (
            <span style={{ marginLeft: 10, color: 'var(--text-secondary)', fontSize: 12 }}>
              {t('catalog.noBrandHint')}
            </span>
          ) : null}
        </div>
      ) : (
        <div
          data-testid={`mapping-policy-${row.source}`}
          style={{
            gridColumn: '4 / 5',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            fontSize: 12.5,
            color: 'var(--text-secondary)',
          }}
        >
          <span style={{ fontWeight: 500 }}>{t('extLedgerUi.policyLabel')}</span>
          <select
            data-testid={`mapping-policy-select-${row.source}`}
            value={row.ledgerPolicy ?? ''}
            disabled={pending}
            onChange={(e) => {
              const v = e.target.value;
              onPickPolicy(v === '' ? null : (v as LedgerPolicy));
            }}
            style={{ height: 32, minWidth: 220 }}
          >
            <option value="">{t('extLedgerUi.policyUnselected')}</option>
            <option value="REFILL">{t('extLedgerUi.policyOptionRefill')}</option>
            <option value="NEW_JUG">{t('extLedgerUi.policyOptionNewJug')}</option>
            <option value="FOREIGN_SWAP">{t('extLedgerUi.policyOptionForeignSwap')}</option>
          </select>
          {row.ledgerPolicy === null ? (
            <span
              data-testid={`mapping-policy-hint-${row.source}`}
              style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}
            >
              {t('extLedgerUi.policyHint')}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * "Yeni ürün olarak ekle" inline formu.
 *
 * Öneri ucu (`/api/channels/mappings/:id/suggestion`) adı, türü ve biliniyorsa son fiyatı
 * döner. Fiyat boşsa kayıt düğmesi kapalıdır (boş fiyat 400 döner). Sunucu 200 dönerse
 * toast basılır ve `onCancel` ile satır kapanır (mapping cache'i createProduct.mutate
 * tarafından invalidate edildiği için satır artık eşleşmiş görünür).
 */
function NewProductForm({
  row,
  defaultBrand,
  brandHasProducts,
  onCancel,
}: {
  row: ProductMapping;
  defaultBrand: Brand | undefined;
  brandHasProducts: boolean;
  onCancel: () => void;
}) {
  const toast = useToast();
  const createProduct = useCreateProductFromMapping();
  const suggestion = useMappingSuggestion(row.id);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'JUG_19L' | 'JUG_15L_GLASS' | 'CASE_05L'>('JUG_19L');
  const [unitPrice, setUnitPrice] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Öneri yüklendiğinde formu bir kez doldur; sonraki öneri değişiklikleri kullanıcının
  // girdiğini EZMESIN (`hydrated` kilidi).
  if (!hydrated && suggestion.data) {
    setName(suggestion.data.name);
    if (suggestion.data.kind) setKind(suggestion.data.kind);
    if (suggestion.data.unitPrice) setUnitPrice(suggestion.data.unitPrice);
    setHydrated(true);
  }

  const jug = returnsEmpty(kind);
  const priceOk = unitPrice.trim() !== '' && parseMoneyInput(unitPrice) !== null;
  const valid = name.trim().length >= 1 && priceOk && !createProduct.isPending;

  function submit(): void {
    const parsedPrice = parseMoneyInput(unitPrice);
    if (!parsedPrice) return;
    createProduct.mutate(
      {
        id: row.id,
        body: {
          name: name.trim(),
          kind,
          unitPrice: parsedPrice,
          ...(jug && depositAmount.trim() !== ''
            ? { depositAmount: depositAmount.replace(',', '.') }
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast(t('catalog.addedAndLinked'));
          onCancel();
        },
      },
    );
  }

  return (
    <div
      data-testid={`mapping-new-form-${row.source}`}
      style={{
        padding: 14,
        borderLeft: '3px solid var(--primary)',
        background: 'var(--surface)',
        borderRadius: 'var(--r-row)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <strong style={{ fontSize: 13.5 }}>{t('catalog.addAsProductTitle')}</strong>
      {suggestion.isLoading ? (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('catalog.suggestionMissing')}
        </span>
      ) : null}
      <div
        className="form-grid"
        style={
          {
            '--form-cols': '1fr 140px 140px 140px',
            '--form-gap': '8px',
            alignItems: 'end',
          } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="section-label">{t('prod.name')}</span>
          <input
            data-testid={`mapping-new-name-${row.source}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ height: 38 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="section-label">{t('prod.kind')}</span>
          <select
            data-testid={`mapping-new-kind-${row.source}`}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'JUG_19L' | 'JUG_15L_GLASS' | 'CASE_05L')}
            style={{ height: 38 }}
          >
            {PRODUCT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`productKind.${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="section-label">{t('prod.unitPrice')}</span>
          <input
            data-testid={`mapping-new-price-${row.source}`}
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            inputMode="decimal"
            style={{ height: 38, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        {jug ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="section-label">{t('prod.deposit')}</span>
            <input
              data-testid={`mapping-new-deposit-${row.source}`}
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              inputMode="decimal"
              style={{ height: 38, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {t('prod.noDepositHint')}
          </span>
        )}
      </div>
      {defaultBrand ? (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Marka: {defaultBrand.code} · {defaultBrand.name}
          {!brandHasProducts ? ' · ' + t('catalog.noBrandHint') : ''}
        </span>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn" onClick={onCancel}>
          {t('catalog.cancelAdd')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          data-testid={`mapping-new-save-${row.source}`}
          disabled={!valid}
          onClick={submit}
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

/** Kaynak (HAYAT_PANEL/ABANT_PANEL) için beklenen marka kodu. Diğer kaynaklarda boş döner. */
function sourceBrandCode(source: string): string | undefined {
  if (source === 'HAYAT_PANEL') return 'H';
  if (source === 'ABANT_PANEL') return 'A';
  return undefined;
}

/** Ürünleri markaya göre gruplar. Anahtar: marka id ya da '__unbranded__'. */
function groupByBrand(products: Product[], brands: Brand[]): Map<string, Product[]> {
  const map = new Map<string, Product[]>();
  // Sıralama: aktif markalar alfabetik, sonra markasızlar.
  for (const b of brands.filter((b) => b.isActive)) map.set(b.id, []);
  map.set('__unbranded__', []);
  for (const p of products) {
    const key = p.brandId ?? '__unbranded__';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  // Boş markaları listeden çıkar; markasız hariç.
  for (const [k, v] of map) {
    if (k === '__unbranded__') continue;
    if (v.length === 0) map.delete(k);
  }
  return map;
}

function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)' }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}

/** Sunucunun bildiği son hata kodları → düz Türkçe. Bilinmeyen ham metin kullanıcıya basılmaz. */
const LAST_ERROR_KEYS: Record<string, Parameters<typeof t>[0]> = {
  'no-credentials': 'ch.err.noCredentials',
  'bad-config': 'ch.err.badConfig',
};

/** Son hata: bilinen kod düz cümle olur; bilinmeyen metin "Teknik ayrıntı" içine katlanır. */
function LastErrorText({ error }: { error: string }) {
  const key = LAST_ERROR_KEYS[error];
  if (key) return <>{t(key)}</>;
  return (
    <>
      {t('ch.err.generic')}
      <details>
        <summary style={{ fontSize: 11.5, cursor: 'pointer' }}>{t('ch.err.technical')}</summary>
        <div style={{ fontSize: 11.5 }}>{error}</div>
      </details>
    </>
  );
}

/**
 * Araç/şoför/çalışan eşlemeleri `gelişmiş ayarlar` editöründe gösterilmez ve sunucuya
 * gönderilmez — bu anahtarları başka ekranlar yönetiyor (Hayat araç eşleme, Abant çalışan
 * eşleme). Gövdeyi filtreleyerek sunucunun mevcut kayıtları korumasını sağlarız; editörün
 * üstündeki not satırı da kullanıcıyı bu sınır hakkında bilgilendirir.
 */
function stripManagedKeys(config: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(config)) {
    if (k === 'vehicleMap' || k === 'driverMap' || k === 'employeeNames') continue;
    out[k] = v;
  }
  return out;
}
